<script setup>
import { onMounted, reactive, ref } from "vue";

import AppCard from "@/components/AppCard";
import AppButton from "@/components/AppButton";
import { axios } from "@/utils/axios";
import { store } from "@/store";
import {
  captureException,
  captureMessage,
  sentryEnabled,
  sentryEnvironment,
} from "@/plugins/sentry";

const results = reactive({});
const running = ref("");

const dsn = process.env.VUE_APP_TOKEN_SENTRY || "";
// Del DSN solo mostramos el proyecto y el host, nunca la clave publica
const project = dsn.split("/").pop();
const host = dsn.split("@").pop().split("/")[0];

const report = (test, message, eventId) => {
  results[test] = {
    message,
    eventId,
    at: new Date().toLocaleTimeString(),
  };
};

// Cada test corre dentro de este wrapper: si algo falla de forma inesperada
// el error queda contenido aqui y la vista sigue usable.
const run = async (test, fn) => {
  running.value = test;
  delete results[test];

  try {
    await fn();
  } catch (error) {
    report(test, `El test lanzo un error no previsto: ${error.message}`);
  } finally {
    running.value = "";
  }
};

// 1. Excepcion enviada a mano. Es la ruta que usa el resto del proyecto
//    cuando ya captura el error en un try/catch.
const testCapturedException = () =>
  run("captured", () => {
    const error = new Error(
      "[Sentry test] Excepcion controlada desde la vista de prueba"
    );
    const eventId = captureException(error, {
      origen: "SentryTest",
      tipo: "captureException",
    });

    report("captured", "Excepcion enviada a Sentry", eventId);
  });

// 2. Mensaje informativo, sin stack trace.
const testMessage = () =>
  run("message", () => {
    const eventId = captureMessage(
      "[Sentry test] Mensaje de verificacion",
      { origen: "SentryTest" },
      "info"
    );

    report("message", "Mensaje enviado a Sentry", eventId);
  });

// 3. Error sin capturar dentro de un handler de Vue. Vue lo desvia a
//    app.config.errorHandler (que instala @sentry/vue), asi que llega a
//    Sentry sin romper el componente ni desmontar la vista.
//    No usa run(): su try/catch se comeria el error antes de que Vue lo vea.
const testVueError = () => {
  delete results.vue;
  report(
    "vue",
    "Error lanzado. Debe aparecer en Sentry via el errorHandler de Vue"
  );

  throw new Error("[Sentry test] Error no capturado en un componente de Vue");
};

// 4. Promesa rechazada sin catch: la recoge el listener de
//    unhandledrejection registrado en main.js.
const testUnhandledRejection = () =>
  run("promise", () => {
    Promise.reject(new Error("[Sentry test] Promesa rechazada sin catch"));

    report("promise", "Promesa rechazada. Debe llegar via unhandledrejection");
  });

// 5. Error de red: lo reporta el interceptor de axios. Apunta a un host
//    inexistente para no tocar la API real.
const testApiError = () =>
  run("api", async () => {
    try {
      await axios.get("https://sentry-test.invalid/endpoint-inexistente", {
        baseURL: "",
        timeout: 8000,
      });

      report("api", "La peticion no fallo, el test no es concluyente");
    } catch (error) {
      report(
        "api",
        `Peticion fallida (${error.message}). Debe llegar via el interceptor de axios`
      );
    }
  });

const tests = [
  {
    key: "captured",
    title: "1. Excepcion capturada",
    description:
      "Envia un Error a Sentry con captureException. Es lo que usan los try/catch del proyecto.",
    action: testCapturedException,
  },
  {
    key: "message",
    title: "2. Mensaje informativo",
    description:
      "Envia un mensaje sin stack trace, para eventos que no son errores.",
    action: testMessage,
  },
  {
    key: "vue",
    title: "3. Error no capturado en Vue",
    description:
      "Lanza un error dentro de un handler. Lo recoge el errorHandler de Vue sin romper la vista.",
    action: testVueError,
  },
  {
    key: "promise",
    title: "4. Promesa rechazada",
    description:
      "Rechaza una promesa sin catch, para verificar el listener de unhandledrejection.",
    action: testUnhandledRejection,
  },
  {
    key: "api",
    title: "5. Error de red en la API",
    description:
      "Peticion a un host inexistente, para verificar el interceptor de axios.",
    action: testApiError,
  },
];

onMounted(() => {
  store.APP_LOADING = false;
});
</script>

<template lang="pug">
.sentry-test
  app-card.sentry-test__status(title="Estado de Sentry")
    .status
      .status__row
        span.status__label Estado
        span.status__value(:class="sentryEnabled ? 'is-ok' : 'is-off'")
          | {{ sentryEnabled ? "Activo" : "Desactivado (falta VUE_APP_TOKEN_SENTRY)" }}
      .status__row
        span.status__label Entorno
        span.status__value {{ sentryEnvironment }}
      .status__row(v-if="sentryEnabled")
        span.status__label Proyecto
        span.status__value {{ project }}
      .status__row(v-if="sentryEnabled")
        span.status__label Servidor
        span.status__value {{ host }}
      .status__row
        span.status__label Usuario asociado
        span.status__value
          | {{ store.user && store.user.id ? `${store.user.name} (${store.user.id})` : "Sin sesion" }}

    .status__hint(v-if="!sentryEnabled")
      | Sentry esta desactivado: los tests se van a registrar en la consola del navegador pero no se enviaran eventos.

  .sentry-test__grid
    app-card(v-for="test in tests" :key="test.key")
      .test
        .test__title {{ test.title }}
        .test__description {{ test.description }}
        .test__action
          app-button(
            :label="running === test.key ? 'Ejecutando...' : 'Ejecutar test'"
            :loading="running === test.key"
            :disabled="running !== ''"
            @click="test.action"
            block
          )
        .test__result(v-if="results[test.key]")
          .test__result-message {{ results[test.key].message }}
          .test__result-meta(v-if="results[test.key].eventId")
            | ID del evento: {{ results[test.key].eventId }}
          .test__result-meta {{ results[test.key].at }}
</template>

<style lang="scss" scoped>
.sentry-test {
  @apply pb-8;

  &__status {
    @apply mb-6;
    @apply cursor-default;
  }

  &__grid {
    @apply grid;
    @apply grid-cols-1;
    @apply gap-4;
    @screen md {
      @apply grid-cols-2;
    }
    @screen xl {
      @apply grid-cols-3;
    }
  }
}

.status {
  @apply text-sm;

  &__row {
    @apply flex;
    @apply justify-between;
    @apply items-center;
    @apply gap-4;
    @apply py-1;
    @apply border-b;
    @apply border-bluegray-100;
    &:last-child {
      @apply border-b-0;
    }
  }

  &__label {
    @apply text-bluegray-400;
  }

  &__value {
    @apply text-bluegray-600;
    @apply font-medium;
    @apply text-right;
    @apply break-all;
  }

  &__hint {
    @apply mt-3;
    @apply text-sm;
    @apply text-yellow-600;
  }

  .is-ok {
    @apply text-green-600;
  }

  .is-off {
    @apply text-red-500;
  }
}

.test {
  @apply flex;
  @apply flex-col;
  @apply h-full;
  @apply cursor-default;

  &__title {
    @apply font-semibold;
    @apply text-bluegray-600;
    @apply mb-1;
  }

  &__description {
    @apply text-sm;
    @apply text-bluegray-400;
    @apply mb-4;
  }

  &__action {
    @apply mt-auto;
  }

  &__result {
    @apply mt-3;
    @apply p-2;
    @apply rounded-md;
    @apply bg-bluegray-50;
    @apply text-sm;
    @apply text-bluegray-600;
  }

  &__result-message {
    @apply break-words;
  }

  &__result-meta {
    @apply mt-1;
    @apply text-xs;
    @apply text-bluegray-400;
    @apply break-all;
  }
}
</style>
